import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select as SingleSelect,
  Switch,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useEffect, useRef } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

import { getCurrentColor } from "../../constants";
import useSearchParamsHandler from "../../hooks/useSearchParamsHandler";
import {
  categoriesPageModeAtom,
  roundsAtom as listingsRoundsAtom,
  shippingAtom as listingsShippingAtom,
  sortByAtom as listingsSortByAtom,
} from "../../atom/listings";
import {
  roundsAtom as productsRoundsAtom,
  shippingAtom as productsShippingAtom,
  sortByAtom as productsSortByAtom,
} from "../../atom/products";
import {
  filterInfoAtom,
  roundsAtom as groupListingsRoundsAtom,
  shippingAtom as groupListingsShippingAtom,
} from "../../atom/groupListings";
import { useAtom } from "jotai";
import { shippingName } from "../../utils/shippingName";

const SelectSort = ({ slider }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colorMode } = useColorMode();
  const { params } = useSearchParamsHandler();
  const [categoriesPageMode, setCategoriesPageMode] = useAtom(
    categoriesPageModeAtom,
  );

  const handleCategoriesPageModeToggle = () => {
    const newMode = categoriesPageMode === "listings" ? "products" : "listings";
    
    setCategoriesPageMode(newMode);
    
    // Convert URL to new SEO format
    const prefix = newMode === "listings" ? "ls" : "pd";
    const pathWithoutPrefix = location.pathname.replace(/^\/(ls|pd)\//, '/');
    const newUrl = `/${prefix}${pathWithoutPrefix}${location.search}`;
    
    // Use React Router's navigate instead of window.history
    navigate(newUrl, { replace: true });
  };

  // Determine mode based on URL for SEO URLs, fallback to atom for legacy URLs
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const firstSegment = pathSegments[0];
  const isSeoUrl = firstSegment && ["ls", "pd", "brands"].includes(firstSegment);
  
  const isListingsMode = isSeoUrl 
    ? firstSegment === "ls" || firstSegment === "brands"  // For SEO URLs, use URL to determine mode
    : categoriesPageMode === "listings";  // For legacy URLs, use atom

  const [sortBy, setSortBy] = useAtom(
    isListingsMode ? listingsSortByAtom : productsSortByAtom,
  );
  const [rounds, setRounds] = useAtom(
    slider
      ? groupListingsRoundsAtom
      : isListingsMode
        ? listingsRoundsAtom
        : productsRoundsAtom,
  );
  const [shipping, setShipping] = useAtom(
    slider
      ? groupListingsShippingAtom
      : isListingsMode
        ? listingsShippingAtom
        : productsShippingAtom,
  );
  const [, setRoundsSlider] = useAtom(groupListingsRoundsAtom);
  const [, setShippingSlider] = useAtom(groupListingsShippingAtom);
  const initialFocusRef = useRef(null);
  const [filters] = useAtom(filterInfoAtom);

  const getPgIdFromPathname = () => {
    const pathnameParts = location.pathname.split("/");
    const lastPart = pathnameParts[pathnameParts.length - 1];
    const pgIdMatch = lastPart.match(/(?:^|-)(\d+)$/);

    return pgIdMatch ? pgIdMatch[1] : null;
  };

  useEffect(() => {
    if (searchParams.get("sort_by") !== sortBy) {
      setSearchParams({ ...params, sort_by: sortBy }, { replace: true });
    }

    if (!slider) {
      setRoundsSlider("");
      setShippingSlider("");
    }

    if (slider) {
      const roundsQueryValue = rounds ? rounds.join(",") : "";
      const shippingQueryValue = shipping ? shipping.join(",") : "";
      const pgId = getPgIdFromPathname();

      if (roundsQueryValue && searchParams.get("rpb") !== roundsQueryValue) {
        setSearchParams({ ...params, rpb: roundsQueryValue }, { replace: true });
      }

      if (shippingQueryValue && searchParams.get("sf") !== shippingQueryValue) {
        setSearchParams({ ...params, sf: shippingQueryValue }, { replace: true });
      }

      if (
        !roundsQueryValue &&
        !shippingQueryValue &&
        (searchParams.get("rpb") || searchParams.get("sf"))
      ) {
        setSearchParams({ sort_by: sortBy, page: params.page, pgId: pgId }, { replace: true });
      }

      if (!roundsQueryValue && shippingQueryValue && searchParams.get("rpb")) {
        setSearchParams({
          sort_by: sortBy,
          sf: shippingQueryValue,
          page: params.page,
          pgId: pgId,
        }, { replace: true });
      }

      if (roundsQueryValue && !shippingQueryValue && searchParams.get("sf")) {
        setSearchParams({
          sort_by: sortBy,
          rpb: roundsQueryValue,
          page: params.page,
          pgId: pgId,
        }, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [sortBy, rounds, shipping]);

  return (
    <Flex gap={2}>
      <>
        {slider && (
          <>
            <Box
              position={"relative"}
              border={"1px solid"}
              borderColor={"blackC.600"}
              borderRadius={"6px"}
              minH={58}
              py={{ base: "0px", md: "8px" }}
              bg={"transparent"}
              cursor={"pointer"}
              display={{ base: "none", md: "block" }}
            >
              {filters && (
                <Select
                  isMulti
                  cursor={"pointer"}
                  border={"none"}
                  _focus={{ outline: "none" }}
                  p={0}
                  pr={"20px"}
                  maxW={"190px"}
                  w={"100%"}
                  position={"relative"}
                  top={"7px"}
                  bg={"transparent"}
                  fontSize={{ base: "10", "1xs": "sm" }}
                  variant={"withoutIcon"}
                  placeholder={"Rounds"}
                  onChange={(selectedOptions) =>
                    setRounds(selectedOptions.map((option) => option.value))
                  }
                  value={
                    rounds
                      ? rounds.map((round) => ({ value: round, label: round }))
                      : []
                  }
                  options={Object.values(filters.rdsperbox)
                    .map((item) => ({
                      value: item.name,
                      label: item.name,
                    }))
                    .sort((a, b) => a.value - b.value)}
                  chakraStyles={{
                    placeholder: (provided) => ({
                      ...provided,
                      color: getCurrentColor(
                        colorMode,
                        "blackC.100",
                        "whiteC.200",
                      ),
                    }),
                  }}
                />
              )}
            </Box>

            <Box
              position={"relative"}
              border={"1px solid"}
              borderColor={"blackC.600"}
              borderRadius={"6px"}
              py={{ base: "0px", md: "8px" }}
              bg={"transparent"}
              cursor={"pointer"}
              display={{ base: "none", md: "block" }}
            >
              {filters && (
                <Select
                  isMulti
                  cursor={"pointer"}
                  border={"none"}
                  _focus={{ outline: "none" }}
                  p={0}
                  pr={"20px"}
                  maxW={"190px"}
                  w={"100%"}
                  position={"relative"}
                  top={"7px"}
                  bg={"transparent"}
                  fontSize={{ base: "10", "1xs": "sm" }}
                  variant={"withoutIcon"}
                  icon={"none"}
                  placeholder={"Shipping"}
                  onChange={(selectedOptions) =>
                    setShipping(selectedOptions.map((option) => option.value))
                  }
                  value={
                    shipping
                      ? shipping.map((item) => ({
                          value: item,
                          label: shippingName(Number(item)),
                        }))
                      : []
                  }
                  options={Object.values(filters.ship_flag).map((item) => ({
                    value: item.name,
                    label: shippingName(Number(item.name)),
                  }))}
                  chakraStyles={{
                    placeholder: (provided) => ({
                      ...provided,
                      color: getCurrentColor(
                        colorMode,
                        "blackC.100",
                        "whiteC.200",
                      ),
                    }),
                    container: (provided) => ({
                      ...provided,
                      position: "relative",
                      top: "50%",
                      transform: "translateY(-50%)",
                      zIndex: 98,
                    }),
                  }}
                />
              )}
            </Box>

            <Box display={{ base: "block", md: "none" }}>
              <Menu
                closeOnSelect={false}
                initialFocusRef={initialFocusRef}
                maxW={"194px"}
              >
                <MenuButton
                  as={Button}
                  border={"1px solid"}
                  borderColor={"blackC.600"}
                  borderRadius={"6px"}
                  px={3}
                  bg={"transparent"}
                  whiteSpace={"nowrap"}
                  h={"100%"}
                  _focus={{ background: "transparent" }}
                  _active={{ background: "transparent" }}
                  rightIcon={
                    <ChevronDownIcon
                      w={"20px"}
                      h={"20px"}
                      color={"grayC.1600"}
                    />
                  }
                >
                  <Text
                    as={"span"}
                    fontSize={"xs"}
                    fontFamily={"mainFont"}
                    color={getCurrentColor(
                      colorMode,
                      "grayC.100",
                      "grayC.1600",
                    )}
                    pr={2}
                  >
                    Filter by
                  </Text>
                </MenuButton>

                <MenuList
                  autoFocus={false}
                  bg={getCurrentColor(colorMode, "whiteC.100", "blueC.500")}
                  minW="0"
                  w="194px"
                >
                  <MenuItem p={0} bg={"transparent"}>
                    <Box width="100%">
                      {filters && (
                        <Select
                          isMulti
                          isSearchable={false}
                          _focus={{ outline: "none" }}
                          border={"none"}
                          placeholder={"Rounds"}
                          onChange={(selectedOptions) =>
                            setRounds(
                              selectedOptions.map((option) => option.value),
                            )
                          }
                          value={
                            rounds
                              ? rounds.map((round) => ({
                                  value: round,
                                  label: round,
                                }))
                              : []
                          }
                          options={Object.values(filters.rdsperbox)
                            .map((item) => ({
                              value: item.name,
                              label: item.name,
                            }))
                            .sort((a, b) => a.value - b.value)}
                          chakraStyles={{
                            placeholder: (provided) => ({
                              ...provided,
                              color: getCurrentColor(
                                colorMode,
                                "blackC.100",
                                "whiteC.200",
                              ),
                            }),
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem p={0} bg={"transparent"}>
                    <Box width="100%">
                      {filters && (
                        <Select
                          isMulti
                          isSearchable={false}
                          _focus={{ outline: "none" }}
                          border={"none"}
                          placeholder={"Shipping"}
                          onChange={(selectedOptions) =>
                            setShipping(
                              selectedOptions.map((option) => option.value),
                            )
                          }
                          value={
                            shipping
                              ? shipping.map((item) => ({
                                  value: item,
                                  label: shippingName(Number(item)),
                                }))
                              : []
                          }
                          options={Object.values(filters.ship_flag).map(
                            (item) => ({
                              value: item.name,
                              label: shippingName(Number(item.name)),
                            }),
                          )}
                          chakraStyles={{
                            placeholder: (provided) => ({
                              ...provided,
                              color: getCurrentColor(
                                colorMode,
                                "blackC.100",
                                "whiteC.200",
                              ),
                            }),
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          </>
        )}
      </>

      {!slider && (
        <Flex
          display={{ base: "none", xl: "flex" }}
          alignItems={"end"}
          gap={2}
          mr={2}
        >
          <Text
            cursor={"pointer"}
            onClick={handleCategoriesPageModeToggle}
            ml={2}
            display={"flex"}
            alignItems={"center"}
            fontWeight={500}
            fontSize={"15px"}
            color={getCurrentColor(
              colorMode,
              categoriesPageMode === "products" ? "grayC.2300" : "blackC.100",
              categoriesPageMode === "products" ? "grayC.2300" : "whiteC.1600",
            )}
          >
            Listings
          </Text>
          <Switch
            colorScheme="blue"
            isChecked={isListingsMode ? false : true}
            onChange={handleCategoriesPageModeToggle}
          />
          <Text
            cursor={"pointer"}
            onClick={handleCategoriesPageModeToggle}
            display={"flex"}
            alignItems={"center"}
            fontWeight={500}
            fontSize={"15px"}
            color={getCurrentColor(
              colorMode,
              categoriesPageMode !== "products" ? "grayC.2300" : "blackC.100",
              categoriesPageMode !== "products" ? "grayC.2300" : "whiteC.100",
            )}
          >
            Products
          </Text>
        </Flex>
      )}

      <Box
        position={"relative"}
        border={"1px solid"}
        borderColor={"blackC.600"}
        borderRadius={"6px"}
        py={{ base: "0px", md: "8px" }}
        bg={"transparent"}
        cursor={"pointer"}
      >
        <Box position={"relative"} top={"50%"} transform="translateY(-50%)">
          <Text
            as={"span"}
            position={"absolute"}
            fontSize={"xs"}
            fontFamily={"mainFont"}
            color={getCurrentColor(colorMode, "grayC.300", "whiteC.100")}
            left={"14px"}
            display={{ base: "none", md: "block" }}
            cursor={"pointer"}
          >
            Sort by
          </Text>

          <ChevronDownIcon
            color={"grayC.1600"}
            position={"absolute"}
            top={"50%"}
            transform={"translateY(-50%)"}
            right={"14px"}
            display={{ base: "none", md: "block" }}
            w={"20px"}
            h={"20px"}
            cursor={"pointer"}
          />
          <SingleSelect
            cursor={"pointer"}
            border={"none"}
            _focus={{ outline: "none" }}
            px={0}
            p={0}
            pr={"20px"}
            maxW={"190px"}
            w={"100%"}
            position={"relative"}
            top={"7px"}
            bg={"transparent"}
            fontSize={{ base: "10", "1xs": "sm" }}
            display={{ base: "none", md: "block" }}
            variant={"withoutIcon"}
            icon={"none"}
            onChange={({ target }) => setSortBy(target.value)}
            value={sortBy}
          >
            <option value="landed_cpr">Total CPR</option>
            <option value="cpr">Item CPR</option>
          </SingleSelect>
          <SingleSelect
            border={"none"}
            _focus={{ outline: "none" }}
            px={0}
            p={0}
            maxW={"190px"}
            w={"100%"}
            position={"relative"}
            bg={"transparent"}
            fontSize={{ base: "10", "1xs": "sm" }}
            display={{ base: "block", md: "none" }}
            placeholder="Sort by"
            onChange={({ target }) => setSortBy(target.value)}
            value={sortBy}
          >
            <option value="landed_cpr">Total CPR</option>
            <option value="cpr">Item CPR</option>
          </SingleSelect>
        </Box>
      </Box>
    </Flex>
  );
};

export default SelectSort;
